<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed } from "vue";

import {
  TemplateDocumentStatus,
  TemplateDocumentType,
  type TemplateDocument,
} from "@/Modules/TemplateDocuments/Models/Domain";

import DocumentIcon from "@/Components/Icons/Document.vue";
import OptionsIcon from "@/Components/Icons/Options.vue";
import TcButton from "@/Components/UI/TcButton.vue";
import TcTag from "@/Components/UI/TcTag.vue";
import TcDropDown, { type DropDownOption } from "@/Components/UI/TcDropDown.vue";
import TcLoader from "@/Components/UI/TcLoader.vue";

import { hasValue } from "@/Utils/Objects";
import { unixSecondsToDateTime } from "@/Utils/Date";

export interface DocumentCardProps {
  document: TemplateDocument;
  isLoading?: boolean;
  isDisabled?: boolean;
  canSelect?: boolean;
}

const props = withDefaults(defineProps<DocumentCardProps>(), {
  canSelect: false,
  isDisabled: false,
  isLoading: false,
});

const emit = defineEmits<{
  (e: "edit", document: TemplateDocument): void;
  (e: "view", document: TemplateDocument): void;
  (e: "publish", document: TemplateDocument): void;
  (e: "history", document: TemplateDocument): void;
  (e: "remove", document: TemplateDocument): void;
  (e: "select", document: TemplateDocument): void;
}>();

const { t } = useI18n();

const isDraft = computed(() => props.document.status === TemplateDocumentStatus.DRAFT);
const isApproved = computed(() => props.document.status === TemplateDocumentStatus.APPROVED);
const type = computed(() => {
  if (props.document.documentType === TemplateDocumentType.CONSENT) {
    return t("documents.editor.type.consent");
  } else if (props.document.documentType === TemplateDocumentType.OTHER) {
    return t("documents.editor.type.other");
  }

  return "";
});

const date = computed(() => {
  if (hasValue(props.document.publishedAt)) {
    return unixSecondsToDateTime(props.document.publishedAt).toFormat("EEE dd MMM yyyy");
  } else {
    return unixSecondsToDateTime(props.document.createdAt).toFormat("EEE dd MMM yyyy");
  }
});

const options = computed((): DropDownOption[] => {
  const base: DropDownOption[] = [];

  base.push({
    id: "edit",
    label: t("documents.templates.card.options.edit"),
    icon: "Edit",
  });

  base.push({
    id: "history",
    label: t("documents.templates.card.options.history"),
    icon: "Redo",
  });

  base.push({
    id: "remove",
    label: t("documents.templates.card.options.remove"),
    icon: "Delete",
  });

  if (isDraft.value) {
    return [
      {
        id: "publish",
        label: t("documents.templates.card.options.publish"),
        icon: "Publish",
      },
      ...base,
    ];
  }

  return base;
});

const handleOptionClick = (option: DropDownOption) => {
  if (option.id === "edit") {
    emit("edit", props.document);
  } else if (option.id === "history") {
    emit("history", props.document);
  } else if (option.id === "remove") {
    emit("remove", props.document);
  } else if (option.id === "publish") {
    emit("publish", props.document);
  }
};
</script>

<template>
  <div
    class="card"
    :class="{
      'card--draft': isDraft,
      'card--selectable': props.canSelect,
      'card--publish': isApproved,
      'card--required': props.document.isRequired,
    }"
  >
    <div class="card__header">
      <DocumentIcon size="xl" />
    </div>

    <div class="card__content">
      <div class="card__content__status">
        <TcTag v-if="isDraft" size="small" background="background-alt-neutral" color="content-mid">
          {{ t("documents.templates.card.status.draft") }}
        </TcTag>

        <TcTag v-else-if="isApproved" size="small" background="secondary-low" color="content-pre-mid">
          {{ t("documents.templates.card.status.approved") }}
        </TcTag>
      </div>

      <p class="card__content__title">
        {{ props.document.currentVersion.title }}
      </p>

      <p class="card__content__type">{{ type }}</p>

      <p class="card__content__date">
        {{ date }}
      </p>

      <TcDropDown
        v-if="!props.canSelect"
        :name="`document_${props.document.uuid}_options`"
        :options="options"
        @click="handleOptionClick"
      >
        <template #opener="{ toggle }">
          <TcTag
            class="card__content__options"
            size="small"
            background="semantic-white"
            @click="
              () => {
                if (props.isDisabled || props.isLoading) return;
                toggle();
              }
            "
          >
            <OptionsIcon v-if="!props.isLoading" size="sm" color="content-high" />
            <TcLoader :size="15" v-else />
          </TcTag>
        </template>
      </TcDropDown>
    </div>

    <div class="card__actions">
      <TcButton
        v-if="props.canSelect"
        variant="outlined"
        isSmall
        :disabled="props.isDisabled"
        @click="emit('select', props.document)"
      >
        {{ t("documents.templates.card.actions.select") }}
      </TcButton>

      <TcButton
        v-else-if="isDraft"
        variant="outlined"
        isSmall
        :disabled="props.isDisabled"
        @click="emit('edit', props.document)"
      >
        {{ t("documents.templates.card.actions.edit") }}
      </TcButton>

      <TcButton
        v-else
        variant="outlined"
        isSmall
        :disabled="props.isDisabled"
        @click="emit('view', props.document)"
      >
        {{ t("documents.templates.card.actions.view") }}
      </TcButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.card {
  @include colors.bg(content, lowest);
  @include colors.border(content, mid, true);
  @include radius(m);
  @include border(2, solid);
  @include sp.padding(y, 4);
  @include sp.padding(x, 5);

  & {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: $space-3;
  }

  @media (max-width: $breakpoint-m) {
    @include sp.padding(y, 2);
    @include sp.padding(x, 3);
  }

  &__header {
    @include sp.padding(y, 5);
    @include colors.bg(background, base);
    @include radius(m);

    display: flex;
    justify-content: center;
  }

  &__content {
    display: grid;
    flex-grow: 2;
    grid-template-areas:
      "status options"
      "title title"
      "type type"
      "date date";
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    gap: $space-3;
    align-items: center;

    &__title {
      @include text.general(high, m);

      grid-area: title;
    }

    &__status {
      grid-area: status;
    }

    &__options {
      grid-area: options;

      &:hover {
        cursor: pointer;
      }
    }

    &__date {
      @include text.general(low, xs);
      @include colors.color(content, high);

      grid-area: date;
    }

    &__type {
      @include sp.padding(y, 1);
      @include sp.padding(x, 2);
      @include colors.bg(primary, low);
      @include text.general(low, xs);
      @include colors.color(content, high);
      @include radius(s);

      grid-area: type;
      width: fit-content;
    }
  }

  &--draft {
    .card {
      &__content {
        &__title,
        &__date {
          @include colors.color(content, mid);
        }
      }
    }
  }

  &--selectable {
    .card {
      &__header {
        @include sp.padding(y, 2);
      }
    }
  }

  &--required {
    &::after {
      content: "*";

      position: absolute;
      top: $space-1;
      right: $space-2;
    }
  }
}
</style>
